import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { 
  Box, 
  Typography, 
  Checkbox, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { formatLastModified } from '../../utilities/dateFormat';
import { Design, SortField, SortDirection } from './DesignsList';

interface DesignsTableProps {
  designs: Design[];
  allSelected: boolean;
  someSelected: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  handleSelectAll: (checked: boolean) => void;
  handleSelectDesign: (uniqueKey: string, checked: boolean) => void;
  handleSort: (field: SortField) => void;
  handleEditDesignByClick: (design: Design) => void;
  getLocationName?: (locationId: string) => string;
}

const DesignsTable: React.FC<DesignsTableProps> = ({
  designs,
  allSelected,
  someSelected,
  sortField,
  sortDirection,
  handleSelectAll,
  handleSelectDesign,
  handleSort,
  handleEditDesignByClick,
  getLocationName,
}) => {
  const theme = useTheme();
  
  const getSortedDesigns = () => {
    return [...designs].sort((a, b) => {
      let aValue = sortField === 'location' ? a.locationId : a[sortField];
      let bValue = sortField === 'location' ? b.locationId : b[sortField];
      
      // Handle null/undefined values
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';
      
      if (sortField === 'updatedAt') {
        aValue = new Date(aValue).getTime().toString();
        bValue = new Date(bValue).getTime().toString();
      }
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  const getSortIcon = (field: typeof sortField) => {
    if (sortField !== field) {
      return <ArrowUpwardIcon sx={{ 
        width: '8px', 
        height: '12px', 
        opacity: 0.3,
        color: theme.palette.text.secondary 
      }} />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUpwardIcon sx={{ 
          width: '8px', 
          height: '12px',
          color: theme.palette.primary.main 
        }} />
      : <ArrowDownwardIcon sx={{ 
          width: '8px', 
          height: '12px',
          color: theme.palette.primary.main 
        }} />;
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: 'none',
        maxHeight: 'calc(100vh - 260px)',
        backgroundColor: theme.palette.background.default,
        overflow: 'auto',

        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.palette.background.default,
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.action.disabled,
          borderRadius: '4px',
          '&:hover': {
            background: theme.palette.action.selected,
          },
        },
      }}
    >
      <StyledTable stickyHeader>
        <TableHead>
          <TableRow>
            <StyledHeaderCell sx={{
              width: '60px',
              padding: '0px 16px'
            }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                sx={{
                  color: theme.palette.action.disabled,
                  '&.Mui-checked': {
                    color: theme.palette.primary.main
                  },
                  '&.MuiCheckbox-indeterminate': {
                    color: theme.palette.primary.main
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '20px'
                  }
                }}
              />
            </StyledHeaderCell>
            <SortableHeaderCell onClick={() => handleSort('project')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Design name
                {getSortIcon('project')}
              </Box>
            </SortableHeaderCell>
            <SortableHeaderCell onClick={() => handleSort('location')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Workplace location
                {getSortIcon('location')}
              </Box>
            </SortableHeaderCell>
            <SortableHeaderCell onClick={() => handleSort('designer')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Designer
                {getSortIcon('designer')}
              </Box>
            </SortableHeaderCell>
            <SortableHeaderCell onClick={() => handleSort('objectType')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Location type
                {getSortIcon('objectType')}
              </Box>
            </SortableHeaderCell>
            <SortableHeaderCell onClick={() => handleSort('updatedAt')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Last modified
                {getSortIcon('updatedAt')}
              </Box>
            </SortableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {getSortedDesigns().length === 0 ? (
            <TableRow>
              <StyledTableCell 
                colSpan={6} 
                sx={{ 
                  textAlign: 'center', 
                  padding: '40px 16px', 
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic' 
                }}
              >
                No designs found
              </StyledTableCell>
            </TableRow>
          ) : (
            getSortedDesigns().map((design, index) => (
              <StyledTableRow 
                key={design.uniqueKey}
                selected={design.selected}
              >
                <StyledTableCell sx={{ width: '60px' }}>
                  <Checkbox
                    checked={design.selected}
                    onChange={(e) => handleSelectDesign(design.uniqueKey, e.target.checked)}
                    sx={{
                      color: theme.palette.action.disabled,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px'
                      }
                    }}
                  />
                </StyledTableCell>
                <ClickableTableCell
                  onClick={() => handleEditDesignByClick(design)}
                >
                    {design.project}
                </ClickableTableCell>
                <StyledTableCell>
                  {getLocationName && design.locationId ? (
                    <Tooltip
                      title={getLocationName(design.locationId)}
                      placement="top"
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                            fontFamily: 'Open Sans'
                          }
                        }
                      }}
                    >
                      <Box
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '300px'
                        }}
                      >
                        {getLocationName(design.locationId)}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      sx={{
                        color: theme.palette.text.secondary
                      }}
                    >
                      No location selected
                    </Box>
                  )}
                </StyledTableCell>
                <StyledTableCell>{design.designer}</StyledTableCell>
                <StyledTableCell>{design.designType}</StyledTableCell>
                <StyledTableCell>{formatLastModified(design.updatedAt)}</StyledTableCell>
              </StyledTableRow>
            ))
          )}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

const StyledTable = styled(Table)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.default,
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& thead .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  '& tbody': {
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  height: '56px',
  padding: '12px 16px',
  fontSize: '14px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  color: theme.palette.text.primary,
  verticalAlign: 'middle',
  borderBottom: `0.6px solid ${theme.palette.divider}`,
}));

const ClickableTableCell = styled(TableCell)(({ theme }) => ({
  height: '56px',
  padding: '12px 16px',
  fontSize: '14px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  color: theme.palette.text.primary,
  verticalAlign: 'middle',
  borderBottom: `0.6px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.primary.main,
  },
}));

const SortableHeaderCell = styled(TableCell)(({ theme }) => ({
  height: '56px',
  padding: '12px 16px',
  fontSize: '14px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  verticalAlign: 'middle',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 10,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.background.default : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:first-of-type .MuiTableCell-root': {
    borderTop: 'none !important',
  },
  '& .MuiTableCell-root': {
    transition: 'background-color 0.2s ease',
  },
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  height: '56px',
  padding: '12px 16px',
  fontSize: '14px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  verticalAlign: 'middle',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

export default DesignsTable;